import React, { useEffect, useState } from 'react';

import { Column, DeliveryPlanRow, MyDeliveryPlanTableProps } from './types';
import {
  ActionButton,
  ActionsContainer,
  CellContentWrapper,
  EmptyTableMessage,
  StyledTable,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableHeaderContainer,
  TableRow,
  TableWrapper,
} from './styles';
import { CustomTooltip } from '../../../components/Tooltip/Tooltip';
import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import SettingsIcon from '../../../../assets/icons/SettingsIcon';
import {
  formatDate,
  formatNumberWithDecimals,
  mapLocaleAccordingToNzRules,
} from '../../../../helpers/localeFormatter';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { useDeliveryPlanStore } from '../../../../stores/deliveryPlanStore';
import { useI18n } from '../../../../hooks/useI18n';
import { useTranslationContext } from '../../../../context/TranslationContext';
import { decodeHtml } from '../../../../helpers/encodingHelper';

const MyDeliveryPlanTable: React.FC<MyDeliveryPlanTableProps> = ({
  tableHeader,
  actions,
  data = [],
  countryIsoCode,
  deliveryPlanVisible,
  deliveryPlanNotVisibleText,
}) => {
  const { isTranslationMode, handleKeyClick, t } = useI18n();
  const { translationVersion } = useTranslationContext();
  const COUNTRIES_WITH_PILE_COVERER = ['de-DE', 'da-DK', 'pl-PL'];
  const { deliveryPlanServiceProviderData } = useDeliveryPlanStore();

  const [updateKey, setUpdateKey] = useState(0);
  const mappedCountry = mapLocaleAccordingToNzRules(countryIsoCode);
  useEffect(() => {
    setUpdateKey((prevKey) => prevKey + 1);
  }, [translationVersion]);

  const FIELD_NAME_COLUMN_NAME = 'fieldName';
  const PILE_COVERER_COLUMN_NAME = 'pileCoverer';
  const TRANSPORTING_COMPANY_COLUMN_NAME = 'transportingCompany';
  const LOAD_DATE_COLUMN_NAME = 'loadDate';
  const AREA_COLUMN_NAME = 'acreage';
  const LOAD_QUANTITY_COLUMN_NAME = 'loadQuantity';

  const getColumns = (): Column[] => {
    const baseColumns: Column[] = [
      {
        field: LOAD_DATE_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_delivery_date),
      },
      {
        field: FIELD_NAME_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_field_name),
      },
      {
        field: AREA_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_area_ha),
        textAlign: 'right',
        width: '100px',
      },
      {
        field: LOAD_QUANTITY_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_load_tons),
        textAlign: 'right',
        width: '100px',
      },
      {
        field: TRANSPORTING_COMPANY_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_transporter),
        hasTooltip: true,
      },
    ];

    if (COUNTRIES_WITH_PILE_COVERER.includes(countryIsoCode || '')) {
      baseColumns.push({
        field: PILE_COVERER_COLUMN_NAME,
        headerName: t(TranslationKeys.my_delivery_plan_column_pile_coverer),
        hasTooltip: true,
      });
    }

    return baseColumns;
  };

  const renderCellContent = (row: DeliveryPlanRow, column: Column) => {
    const value = row[column.field];
    let displayValue: string | React.ReactNode = '-';
    const tooltipShouldBeRendered = column.hasTooltip;
    if (value !== null && value !== undefined) {
      switch (column.field) {
        case LOAD_DATE_COLUMN_NAME:
          if (row.exactDateVisible) {
            displayValue = formatDate(value as string, mappedCountry);
          } else {
            displayValue = isTranslationMode ? (
              <span
                style={{ background: '#97B053', color: 'black' }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleKeyClick(TranslationKeys.calendar_week);
                }}
              >
                {TranslationKeys.calendar_week}
              </span>
            ) : (
              `${t(TranslationKeys.calendar_week)} ${row.loadWeek}`
            );
          }
          break;
        case LOAD_QUANTITY_COLUMN_NAME:
          displayValue = formatNumberWithDecimals(
            value as number,
            mappedCountry,
            0
          );
          break;
        case AREA_COLUMN_NAME:
          displayValue = formatNumberWithDecimals(
            value as number,
            mappedCountry,
            1
          );
          break;
        case FIELD_NAME_COLUMN_NAME:
          displayValue = row.isSelfLoader
            ? t(TranslationKeys.my_delivery_plan_own_choice)
            : String(value);
          break;
        case TRANSPORTING_COMPANY_COLUMN_NAME:
          displayValue = row.transportingCompanyName;
          break;
        case PILE_COVERER_COLUMN_NAME:
          displayValue = row.pileCovererName;
          break;
        default:
          displayValue = String(value);
      }
    }
    if (tooltipShouldBeRendered) {
      return (
        <CellContentWrapper>
          <span>{displayValue}</span>
          {tooltipShouldBeRendered && (
            <CustomTooltip
              placement="left-start"
              asyncLoadText={() => {
                return new Promise<string>((resolve) => {
                  if (value) {
                    deliveryPlanServiceProviderData(value?.toString())
                      .then((r) => {
                        resolve(
                          r
                            ? decodeHtml(r)
                            : (t(
                                TranslationKeys.delivery_plan_no_additional_info
                              ) as string)
                        );
                      })
                      .catch((e) => {
                        console.error(e);
                        resolve(
                          t(
                            TranslationKeys.delivery_plan_no_additional_info
                          ) as string
                        );
                      });
                  } else {
                    resolve(
                      t(
                        TranslationKeys.delivery_plan_no_additional_info
                      ) as string
                    );
                  }
                });
              }}
              text=""
            />
          )}
        </CellContentWrapper>
      );
    }

    return (
      <CellContentWrapper textAlign={column.textAlign}>
        <span>{displayValue}</span>
      </CellContentWrapper>
    );
  };

  const renderDeliveryPlanNotVisibleInfo = () => {
    return (
      <TableWrapper isTranslationMode={isTranslationMode}>
        <TableContainer>
          <StyledTable>
            <tbody>
              <TableRow>
                <EmptyTableMessage colSpan={getColumns().length}>
                  {deliveryPlanNotVisibleText}
                </EmptyTableMessage>
              </TableRow>
            </tbody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    );
  };

  const renderDeliveryPlanData = (d: DeliveryPlanRow[]) => {
    const shouldShowActions = actions?.showDownload || actions?.showSettings;
    return (
      <TableWrapper key={updateKey} isTranslationMode={isTranslationMode}>
        {(tableHeader || shouldShowActions) && (
          <TableHeaderContainer>
            {tableHeader && <h2>{tableHeader}</h2>}
            {shouldShowActions && (
              <ActionsContainer>
                {actions?.showDownload && (
                  <ActionButton
                    onClick={actions.onDownloadClick}
                    title={
                      t(TranslationKeys.my_delivery_plan_download) as string
                    }
                  >
                    <DownloadIcon isIsolatedIcon />
                  </ActionButton>
                )}
                {actions?.showSettings && (
                  <ActionButton
                    onClick={actions.onSettingsClick}
                    title={
                      t(TranslationKeys.my_delivery_plan_settings) as string
                    }
                  >
                    <SettingsIcon />
                  </ActionButton>
                )}
              </ActionsContainer>
            )}
          </TableHeaderContainer>
        )}
        <TableContainer>
          <StyledTable>
            <thead>
              <TableRow>
                {getColumns().map((column) => (
                  <TableHeader key={column.field} width={column.width}>
                    {column.headerName}
                  </TableHeader>
                ))}
              </TableRow>
            </thead>
            <tbody>
              {d.length === 0 ? (
                <TableRow>
                  <EmptyTableMessage colSpan={getColumns().length}>
                    {t(TranslationKeys.my_delivery_plan_no_planned_deliveries)}
                  </EmptyTableMessage>
                </TableRow>
              ) : (
                d.map((row, index) => (
                  <TableRow key={index}>
                    {getColumns().map((column) => (
                      <TableCell
                        key={`${index}-${column.field}`}
                        textAlign={column.textAlign}
                      >
                        {renderCellContent(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </tbody>
          </StyledTable>
        </TableContainer>
        <TableFooter>
          {t(TranslationKeys.delivery_plan_table_footer)}
        </TableFooter>
      </TableWrapper>
    );
  };

  return deliveryPlanVisible
    ? renderDeliveryPlanData(data)
    : renderDeliveryPlanNotVisibleInfo();
};

export default MyDeliveryPlanTable;
